import { useEffect, useRef, useState, useCallback, memo } from "react";
import api from "../../api/api";
import Swal from "sweetalert2";
import styles from "./ClientePage.module.scss";
import logoFallback from "../../assets/logo.png";

// ─── Constants ────────────────────────────────────────────────────────────────

// Cloudinary já retorna a URL completa — BASE_URL não é mais necessário

const PRINT_FORMAT_LABELS = {
  a4:     "A4",
  bobina: "Bobina",
  cupom:  "Cupom",
};

const PRINT_FORMATS  = ["a4", "bobina", "cupom"];
const BOBINA_WIDTHS  = ["58mm", "80mm"];

// ─── Service ──────────────────────────────────────────────────────────────────

const clienteService = {
  get: () =>
    api.get("/clientes"),

  update: (id, payload) =>
    api.put(`/clientes/${id}`, payload),

  removeLogo: (id, clienteData) =>
    api.put(`/clientes/${id}`, { ...clienteData, logo_url: null }),

  uploadLogo: (id, file) => {
    const formData = new FormData();
    formData.append("logo", file);
    return api.post(`/clientes/${id}/upload-logo`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

const useCliente = () => {
  const [cliente,  setCliente]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await clienteService.get();
      setCliente(data);
    } catch {
      setError("Não foi possível carregar os dados do cliente.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { cliente, setCliente, loading, error, refetch: fetch };
};

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Item de informação com label + valor */
const InfoItem = memo(({ label, value, icon }) => (
  <div className={styles.infoItem}>
    {icon && <span className={styles.infoIcon} aria-hidden="true">{icon}</span>}
    <div className={styles.infoContent}>
      <span className={styles.infoLabel}>{label}</span>
      <strong className={styles.infoValue}>{value || "—"}</strong>
    </div>
  </div>
));
InfoItem.displayName = "InfoItem";

/** Card de preferência de impressão */
const PrintPref = memo(({ label, value }) => (
  <div className={styles.printPref}>
    <span className={styles.printLabel}>{label}</span>
    <strong className={styles.printValue}>{value || "—"}</strong>
  </div>
));
PrintPref.displayName = "PrintPref";

/** Skeleton de loading */
const Skeleton = () => (
  <div className={styles.skeleton} aria-hidden="true">
    <div className={styles.skeletonAvatar} />
    <div className={styles.skeletonLines}>
      <div className={styles.skeletonLine} style={{ width: "60%" }} />
      <div className={styles.skeletonLine} style={{ width: "40%" }} />
    </div>
  </div>
);

/** Modal de edição nativo (substitui Swal para o form) */
const EditModal = memo(({ cliente, onSave, onClose }) => {
  const [form, setForm] = useState({
    nome:           cliente.nome           ?? "",
    cnpj_cpf:       cliente.cnpj_cpf       ?? "",
    email:          cliente.email          ?? "",
    telefone:       cliente.telefone       ?? "",
    endereco:       cliente.endereco       ?? "",
    tipo_impressao: cliente.tipo_impressao ?? "a4",
    largura_bobina: cliente.largura_bobina ?? "80mm",
    auto_imprimir:  cliente.auto_imprimir  ?? false,
  });
  const [saving, setSaving] = useState(false);

  const handleChange = useCallback(
    (field) => (e) => {
      const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await onSave({
        ...form,
        auto_imprimir: form.auto_imprimir === "true" || form.auto_imprimir === true,
        ativo:     cliente.ativo,
        logo_url:  cliente.logo_url,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-modal-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 id="edit-modal-title" className={styles.modalTitle}>Editar Informações</h2>
          <button
            type="button"
            className={styles.btnClose}
            onClick={onClose}
            aria-label="Fechar"
            disabled={saving}
          >
            <CloseIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalForm} noValidate>
          <div className={styles.formGrid}>
            <FormField id="nome" label="Nome" required>
              <input id="nome" type="text" className={styles.input}
                value={form.nome} onChange={handleChange("nome")}
                placeholder="Nome da empresa" required disabled={saving} />
            </FormField>

            <FormField id="cnpj_cpf" label="CNPJ / CPF" required>
              <input id="cnpj_cpf" type="text" className={styles.input}
                value={form.cnpj_cpf} onChange={handleChange("cnpj_cpf")}
                placeholder="00.000.000/0000-00" required disabled={saving} />
            </FormField>

            <FormField id="email" label="E-mail" required>
              <input id="email" type="email" className={styles.input}
                value={form.email} onChange={handleChange("email")}
                placeholder="contato@empresa.com" required disabled={saving} />
            </FormField>

            <FormField id="telefone" label="Telefone">
              <input id="telefone" type="tel" className={styles.input}
                value={form.telefone} onChange={handleChange("telefone")}
                placeholder="(11) 90000-0000" disabled={saving} />
            </FormField>

            <FormField id="endereco" label="Endereço" fullWidth>
              <input id="endereco" type="text" className={styles.input}
                value={form.endereco} onChange={handleChange("endereco")}
                placeholder="Rua, número, cidade" disabled={saving} />
            </FormField>

            <div className={styles.divider} />

            <FormField id="tipo_impressao" label="Formato de impressão">
              <select id="tipo_impressao" className={styles.select}
                value={form.tipo_impressao} onChange={handleChange("tipo_impressao")}
                disabled={saving}>
                {PRINT_FORMATS.map((f) => (
                  <option key={f} value={f}>{PRINT_FORMAT_LABELS[f]}</option>
                ))}
              </select>
            </FormField>

            <FormField id="largura_bobina" label="Largura da bobina">
              <select id="largura_bobina" className={styles.select}
                value={form.largura_bobina} onChange={handleChange("largura_bobina")}
                disabled={saving}>
                {BOBINA_WIDTHS.map((w) => (
                  <option key={w} value={w}>{w}</option>
                ))}
              </select>
            </FormField>

            <FormField id="auto_imprimir" label="Modo de impressão" fullWidth>
              <select id="auto_imprimir" className={styles.select}
                value={String(form.auto_imprimir)} onChange={handleChange("auto_imprimir")}
                disabled={saving}>
                <option value="true">Automático</option>
                <option value="false">Confirmar antes</option>
              </select>
            </FormField>
          </div>

          <div className={styles.modalFooter}>
            <button type="button" className={styles.btnCancel}
              onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className={styles.btnSave}
              disabled={saving} aria-busy={saving}>
              {saving
                ? <><span className={styles.spinner} aria-hidden="true" /> Salvando...</>
                : "Salvar alterações"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});
EditModal.displayName = "EditModal";

/** Campo de formulário genérico */
const FormField = memo(({ id, label, children, required, fullWidth }) => (
  <div className={`${styles.formGroup} ${fullWidth ? styles.fullWidth : ""}`}>
    <label htmlFor={id} className={styles.formLabel}>
      {label}
      {required && <span className={styles.required} aria-hidden="true"> *</span>}
    </label>
    {children}
  </div>
));
FormField.displayName = "FormField";

// ─── Icons ────────────────────────────────────────────────────────────────────

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const ClientePage = () => {
  const { cliente, setCliente, loading, error, refetch } = useCliente();
  const [editOpen,       setEditOpen]       = useState(false);
  const [uploadingLogo,  setUploadingLogo]  = useState(false);
  const fileInputRef = useRef(null);

  // ── Logo upload ─────────────────────────────────────────────────────────────
  const handleLogoUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file || !cliente) return;

    // Reset para permitir re-upload do mesmo arquivo
    e.target.value = "";

    // Validação básica no cliente antes de enviar
    const MAX_MB = 5;
    if (file.size > MAX_MB * 1024 * 1024) {
      Swal.fire("Arquivo muito grande", `A logo deve ter no máximo ${MAX_MB}MB.`, "warning");
      return;
    }
    if (!file.type.startsWith("image/")) {
      Swal.fire("Formato inválido", "Selecione um arquivo de imagem (PNG, JPG, WebP...).", "warning");
      return;
    }

    console.log("[uploadLogo] Iniciando upload:", { clienteId: cliente.id, fileName: file.name, fileType: file.type, fileSize: file.size });

    setUploadingLogo(true);
    try {
      const response = await clienteService.uploadLogo(cliente.id, file);
      console.log("[uploadLogo] Resposta completa da API:", response);
      console.log("[uploadLogo] response.data:", response.data);

      const { data } = response;

      if (!data?.logo_url) {
        console.error("[uploadLogo] logo_url ausente na resposta. Chaves recebidas:", Object.keys(data ?? {}));
        throw new Error(`A API não retornou logo_url. Dados recebidos: ${JSON.stringify(data)}`);
      }

      console.log("[uploadLogo] logo_url recebida:", data.logo_url);
      setCliente((prev) => ({ ...prev, logo_url: data.logo_url }));
      Swal.fire({ icon: "success", title: "Logo atualizada!", timer: 1400, showConfirmButton: false });
    } catch (err) {
      const detail = err.response?.data?.error || err.message || "Erro desconhecido";
      console.error("[uploadLogo] ERRO:", err);
      console.error("[uploadLogo] err.response:", err.response);
      Swal.fire("Erro ao enviar logo", detail, "error");
    } finally {
      setUploadingLogo(false);
    }
  }, [cliente, setCliente]);

  // ── Remove logo ─────────────────────────────────────────────────────────────
  const handleRemoveLogo = useCallback(async () => {
    if (!cliente) return;

    const result = await Swal.fire({
      title: "Remover logo?",
      text: "A logo atual será removida.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E53E3E",
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      const { data } = await clienteService.removeLogo(cliente.id, cliente);
      setCliente(data);
      Swal.fire({ icon: "success", title: "Logo removida!", timer: 1200, showConfirmButton: false });
    } catch {
      Swal.fire("Erro", "Não foi possível remover a logo.", "error");
    }
  }, [cliente, setCliente]);

  // ── Save edit ───────────────────────────────────────────────────────────────
  const handleSave = useCallback(async (payload) => {
    try {
      const { data } = await clienteService.update(cliente.id, payload);
      setCliente(data);
      setEditOpen(false);
      Swal.fire({ icon: "success", title: "Dados atualizados!", timer: 1400, showConfirmButton: false });
    } catch {
      Swal.fire("Erro", "Não foi possível atualizar.", "error");
    }
  }, [cliente, setCliente]);

  // ── Render states ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <Skeleton />
        </div>
      </div>
    );
  }

  if (error || !cliente) {
    return (
      <div className={styles.page}>
        <div className={styles.errorBox} role="alert">
          <p>{error ?? "Cliente não encontrado."}</p>
          <button className={styles.btnRetry} onClick={refetch}>Tentar novamente</button>
        </div>
      </div>
    );
  }

  const logoSrc = cliente.logo_url
    ? cliente.logo_url        // URL completa do Cloudinary
    : logoFallback;

  return (
    <div className={styles.page}>
      <div className={styles.card}>

        {/* ── Header: logo + identidade ───────────────────────────────────── */}
        <div className={styles.cardHeader}>
          <div className={styles.logoSection}>
            {/* Logo area */}
            <div className={styles.logoWrapper}>
              <button
                type="button"
                className={`${styles.logoBtn} ${uploadingLogo ? styles.logoUploading : ""}`}
                onClick={() => fileInputRef.current?.click()}
                aria-label={cliente.logo_url ? "Alterar logo" : "Adicionar logo"}
                title={cliente.logo_url ? "Clique para alterar a logo" : "Clique para adicionar uma logo"}
                disabled={uploadingLogo}
              >
                {uploadingLogo ? (
                  <span className={styles.spinner} aria-hidden="true" />
                ) : (
                  <img
                    src={logoSrc}
                    alt={cliente.logo_url ? `Logo de ${cliente.nome}` : "Logo padrão"}
                    className={`${styles.logoImg} ${!cliente.logo_url ? styles.logoImgFallback : ""}`}
                  />
                )}
              </button>

              {cliente.logo_url && (
                <button
                  type="button"
                  className={styles.btnRemoveLogo}
                  onClick={handleRemoveLogo}
                  aria-label="Remover logo"
                  title="Remover logo"
                >
                  <TrashIcon /> Remover
                </button>
              )}

              <input
                type="file"
                hidden
                ref={fileInputRef}
                accept="image/*"
                onChange={handleLogoUpload}
                aria-label="Selecionar logo"
              />
            </div>

            {/* Identity */}
            <div className={styles.identity}>
              <h1 className={styles.clienteName}>{cliente.nome}</h1>
              <p className={styles.clienteSubtitle}>Gerencie seus dados e preferências de impressão</p>
              <span className={`${styles.activeBadge} ${cliente.ativo ? styles.active : styles.inactive}`}>
                <span className={styles.activeDot} />
                {cliente.ativo ? "Ativo" : "Inativo"}
              </span>
            </div>
          </div>

          <button
            type="button"
            className={styles.btnEdit}
            onClick={() => setEditOpen(true)}
            aria-label="Editar informações do cliente"
          >
            <EditIcon /> Editar informações
          </button>
        </div>

        {/* ── Info grid ───────────────────────────────────────────────────── */}
        <section aria-label="Dados cadastrais">
          <h2 className={styles.sectionTitle}>Dados cadastrais</h2>
          <div className={styles.infoGrid}>
            <InfoItem label="CNPJ / CPF"  value={cliente.cnpj_cpf}  icon={<DocIcon />} />
            <InfoItem label="E-mail"       value={cliente.email}      icon={<MailIcon />} />
            <InfoItem label="Telefone"     value={cliente.telefone}   icon={<PhoneIcon />} />
            <InfoItem label="Endereço"     value={cliente.endereco}   icon={<PinIcon />} />
          </div>
        </section>

        {/* ── Print preferences ───────────────────────────────────────────── */}
        <section aria-label="Preferências de impressão">
          <h2 className={styles.sectionTitle}>Preferências de impressão</h2>
          <div className={styles.printGrid}>
            <PrintPref label="Formato"
              value={PRINT_FORMAT_LABELS[cliente.tipo_impressao] ?? cliente.tipo_impressao} />
            <PrintPref label="Largura da bobina" value={cliente.largura_bobina} />
            <PrintPref label="Modo"
              value={cliente.auto_imprimir ? "Automático" : "Confirmar antes"} />
          </div>
        </section>

      </div>

      {/* ── Edit modal ──────────────────────────────────────────────────────── */}
      {editOpen && (
        <EditModal
          cliente={cliente}
          onSave={handleSave}
          onClose={() => setEditOpen(false)}
        />
      )}
    </div>
  );
};

// ── Inline icons (simples, sem dependência extra) ─────────────────────────────
const DocIcon   = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
  </svg>
);
const MailIcon  = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);
const PhoneIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.58 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);
const PinIcon   = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

export default ClientePage;